import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { faChevronRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faTrash } from '@soundxyz/font-awesome/sharp-solid-svg-icons/index.mjs';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { ResendVerificationEmail } from '@components/EmailNotifications/ResendVerificationEmail';
import {
  useEmailNotifications,
  useEmailNotificationsForm,
} from '@components/EmailNotifications/hooks';
import { ERRORS } from '@components/EmailNotifications/schema';
import { getStatusMessage } from '@components/EmailNotifications/shared';
import { Input, InputWrapper } from '@components/basics/forms/inputs/Input';
import { Button } from '@components/main-components/button/Button';
import { NotificationsCircle } from '@components/main-components/media/illustrations/NotificationsCircle';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { StandardModalButton } from '@components/main-components/modals/StandardModalButton';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  EmailVerificationStatus,
  GetUserNotificationPreferencesDocument,
  SetReceiveArtistUpdateEmailsDocument,
  UpdateNotificationEmailDocument,
  useMutation,
} from '@graphql/reactQuery';
import { NAV_LINKS } from '@utils/constants';

RefetchOnComplete({
  trigger: [UpdateNotificationEmailDocument, SetReceiveArtistUpdateEmailsDocument],
  refetch: [GetUserNotificationPreferencesDocument],
});

export const EmailNotificationInputModal = ({
  artistName,
  onVerify,
}: {
  artistName?: string;
  onVerify?: () => void;
}) => {
  const { openToast } = useToast();
  const { closeCancelModal } = useModal();
  const { loading, updateEmail, updatingEmail, status, email } = useEmailNotifications();
  const {
    fields,
    setField,
    clearErrors,
    touched,
    validate,
    errors,
    setError,
    setAllFieldsTouched,
  } = useEmailNotificationsForm();

  const { mutateAsync: toggleReceiveArtistEmails } = useMutation(
    SetReceiveArtistUpdateEmailsDocument,
    {},
  );

  const onClick = useStableCallback(async () => {
    try {
      setAllFieldsTouched();
      const result = validate();

      if (!result) return;

      await updateEmail({
        email: result.email,
        onVerify,
        onClaimedError: () => {
          setError('email', ERRORS.email.non_unique);
        },
      });

      toggleReceiveArtistEmails({
        enable: true,
      });
    } catch (error) {
      openToast({
        text: error instanceof Error ? error.message : 'Unknown error',
        variant: 'error',
      });
    }
  });

  const clearEmail = useStableCallback(async () => {
    try {
      clearErrors();
      setField('email', '');
      await updateEmail({
        email: null,
        onVerify,
      });
    } catch (error) {
      openToast({
        text: error instanceof Error ? error.message : 'Unknown error',
        variant: 'error',
      });
    }
  });

  const getMessage = useStableCallback(() => {
    return artistName ? (
      <>We'll let you know when {artistName} drops a new song.</>
    ) : (
      <>
        We'll let you know when your
        <br />
        favorite artists drop a new song.
      </>
    );
  });

  const expiredOrUnverified = useMemo(() => {
    return status === EmailVerificationStatus.Expired || status === EmailVerificationStatus.Pending;
  }, [status]);

  const disableVerify = useMemo(() => {
    return fields.email === '' || !touched.email || !!errors.email;
  }, [fields.email, touched.email, errors.email]);

  const body = (
    <div className="flex flex-col items-center justify-center">
      <NotificationsCircle className="hidden md:block" />

      <div className="mb-8 flex flex-col items-center justify-center gap-2">
        <h2 className="font-title text-title-l font-medium text-base800">
          Stay updated on new drops
        </h2>
        <p className="text-base text-center text-base-l text-base800">{getMessage()}</p>
      </div>

      <InputWrapper
        className="flex w-full flex-col gap-y-2"
        error={touched.email && !!errors.email}
        disabled={loading}
      >
        <div className="mb-1 flex w-full items-stretch gap-x-2">
          <Input
            type="email"
            value={fields.email}
            placeholder="Your Email Address"
            disabled={loading || updatingEmail || EmailVerificationStatus.Pending === status}
            onChange={ev => {
              setField('email', ev.target.value, true);
              validate();
            }}
          />
          {/* Tablet+ Button */}
          {expiredOrUnverified ? (
            <Button
              label=""
              onClick={clearEmail}
              variant="tertiary"
              className="h-auto self-stretch"
              size="squared"
              loading={loading || updatingEmail}
              leadingIcon={<FontAwesomeIcon icon={faTrash} />}
              iconOnly
            />
          ) : (
            <>
              <Button
                label="Verify"
                onClick={onClick}
                variant="primary"
                className="hidden w-24 min-w-[6rem] md:block"
                loading={loading || updatingEmail}
                disabled={disableVerify}
              />
              {/* Mobile Button */}
              <Button
                label=""
                onClick={onClick}
                variant="primary"
                className="md:hidden"
                loading={loading || updatingEmail}
                disabled={disableVerify}
                leadingIcon={<FontAwesomeIcon icon={faChevronRight} />}
                size="squared"
                iconOnly
              />
            </>
          )}
        </div>
        <InputWrapper.Error errorMessage={errors.email || ''} />
        {!errors.email && (
          <div
            className={twMerge(
              '-mt-1 items-center font-base !text-base-s',
              loading || !!email ? 'text-base500' : 'text-base800',
            )}
          >
            {getStatusMessage({ status })}
          </div>
        )}
      </InputWrapper>
    </div>
  );

  return <StandardModal body={body} closeModal={closeCancelModal} size="small" />;
};

export const EmailNotificationConfirmationModal = ({ email }: { email: string }) => {
  const { closeCancelModal } = useModal();

  const body = (
    <div className="flex flex-col items-center justify-center">
      <div className="mt-2 flex flex-col items-center justify-center gap-2">
        <h2 className="text-center font-title text-title-l font-medium text-base800">
          Verify your email address
        </h2>
        <p className="text-center font-base text-base-l text-base800">
          An email has been sent to {email}.
        </p>

        <p className="mt-2 text-center font-base text-base-l text-base800">
          Didn't receive our email?
          <br />
          Check your Spam folder or <ResendVerificationEmail confirmationModal />
        </p>
      </div>
    </div>
  );

  return (
    <StandardModal
      body={body}
      closeModal={closeCancelModal}
      size="small"
      callToAction={
        <StandardModalButton label="Close" variant="primary" onClick={closeCancelModal} />
      }
      backCallToAction={
        <StandardModalButton
          label="Manage Settings"
          variant="secondary"
          href={NAV_LINKS.NotificationsDashboard}
        />
      }
    />
  );
};
